import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { LiveToolRunner } from '../../../components/prioritization/session/LiveToolRunner';
import type { ToolItem } from '../../../contexts/ToolItemsContext';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { C } from '../theme/participantTokens';
import { LayerBack, LayerBar } from './layout/ParticipantLayout';

interface ParticipantAsyncToolRoomProps {
  session: PrioritizationSession;
  onBack: () => void;
  onComplete: () => void;
}

export function ParticipantAsyncToolRoom({
  session,
  onBack,
  onComplete,
}: ParticipantAsyncToolRoomProps) {
  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? 'Participant';
  const allItems = useItemsStore(s => s.items);

  const [toolSessionId, setToolSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showingTool, setShowingTool] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Items changed after this person voted, under the remediate rule (migration
  // 1063): they answer only these; the database keeps their other answers.
  const [onlyNewItemIds, setOnlyNewItemIds] = useState<string[] | null>(null);
  // The tool takes its items once, when it opens, so it waits for this lookup.
  const [pendingChecked, setPendingChecked] = useState(false);

  const allToolItems: ToolItem[] = useMemo(() => {
    // 1. session.itemIds — populated for traditional and V3 platform-import sessions
    if ((session.itemIds ?? []).length > 0) {
      const fromWorkspace = allItems
        .filter(i => session.itemIds.includes(i.id))
        .map(i => ({
          id: i.id,
          name: i.title ?? 'Item',
          description: i.description ?? '',
          sourceId: i.id,
        }));
      if (fromWorkspace.length > 0) return fromWorkspace;
    }

    // 2. v3Config.items — V3 events persist items here (platform or typed)
    const modeConfig = (session.modeConfig as Record<string, unknown>)?.v3Config as
      | { items?: Array<{ id: string; title?: string; name?: string }>; primaryGoalStep?: string; criteria?: Array<{ id: string; name: string }> }
      | undefined;

    // 2a. S3 criteria sessions — items are criteria, not workspace items
    if (modeConfig?.primaryGoalStep === 'S3' && (modeConfig?.criteria ?? []).length > 0) {
      return (modeConfig.criteria ?? []).map(c => ({
        id: c.id,
        name: c.name,
        description: '',
        sourceId: c.id,
      }));
    }

    // 2b. Regular V3 items
    if ((modeConfig?.items ?? []).length > 0) {
      return (modeConfig!.items ?? []).map(i => ({
        id: i.id,
        name: i.title ?? i.name ?? 'Item',
        description: '',
        sourceId: i.id,
      }));
    }

    return [];
  }, [session.itemIds, session.modeConfig, allItems]);

  const sessionToolItems: ToolItem[] = useMemo(
    () => (onlyNewItemIds ? allToolItems.filter(i => onlyNewItemIds.includes(i.id)) : allToolItems),
    [allToolItems, onlyNewItemIds],
  );

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !toolSessionId || !userId) {
      setPendingChecked(true);
      return;
    }
    let cancelled = false;
    supabase
      .from('tool_session_responses')
      .select('source_metadata')
      .eq('tool_session_id', toolSessionId)
      .eq('participant_id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const pending = (data?.source_metadata as { remediate_item_ids?: unknown } | null)?.remediate_item_ids;
        setOnlyNewItemIds(Array.isArray(pending) && pending.length > 0 ? pending.map(String) : null);
        setPendingChecked(true);
      }, () => {
        if (!cancelled) setPendingChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [toolSessionId, userId]);

  const workspaceId = getCurrentWorkspaceId() ?? session.workspaceId ?? '';

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !session.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    supabase
      .from('tool_sessions')
      .select('id')
      .eq('session_id', session.id)
      .in('phase', ['collecting', 'revealed'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) setError(err.message);
        if (data) setToolSessionId((data as { id: string }).id);
        setLoading(false);
      });

    const channel = supabase
      .channel(`participant-tool-session:${session.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tool_sessions', filter: `session_id=eq.${session.id}` },
        payload => {
          const row = payload.new as { id: string; phase: string };
          if (row.phase === 'collecting' || row.phase === 'revealed') {
            setToolSessionId(row.id);
            setLoading(false);
            setError(null);
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [session.id]);

  const handleComplete = useCallback(() => {
    setShowingTool(false);
    onComplete();
  }, [onComplete]);

  if (loading || (toolSessionId && !pendingChecked)) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: C.textTer }} />
      </div>
    );
  }

  if (error || !toolSessionId) {
    return (
      <>
        <LayerBar>
          <LayerBack label="Brief" onClick={onBack} />
          <div style={{ padding: '0 16px', fontFamily: 'Spectral, Georgia, serif', fontSize: '0.88rem', color: C.textSec }}>
            {session.name}
          </div>
        </LayerBar>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32, textAlign: 'center' }}>
          <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.9rem', color: C.textSec }}>
            Waiting for the facilitator to open this session…
          </p>
          <p style={{ fontFamily: 'JetBrains Mono', fontSize: '0.55rem', color: C.textDim, letterSpacing: '0.1em' }}>
            This page updates automatically
          </p>
        </div>
      </>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <LayerBar>
        <LayerBack label="Brief" onClick={onBack} />
        <div style={{ padding: '0 16px', fontFamily: 'Spectral, Georgia, serif', fontSize: '0.88rem', color: C.textSec }}>
          {session.name}
        </div>
      </LayerBar>
      {showingTool && onlyNewItemIds && (
        <p style={{ margin: 0, padding: '10px 16px', fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', color: C.textSec, borderBottom: `1px solid ${C.rule}` }}>
          {sessionToolItems.length === 1 ? 'An item was' : `${sessionToolItems.length} items were`} added after you voted.
          Your answers on the other items are kept; vote on {sessionToolItems.length === 1 ? 'this one' : 'these'} only.
        </p>
      )}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', background: C.bg }}>
        {showingTool && toolSessionId && (
          <LiveToolRunner
            embedded
            sessionId={session.id}
            toolSessionId={toolSessionId}
            workspaceId={workspaceId}
            selectedToolId={session.selectedToolId}
            toolConfig={session.toolConfig}
            items={sessionToolItems}
            participantId={userId}
            participantName={userName}
            onComplete={handleComplete}
            onBack={onBack}
          />
        )}
        {!showingTool && (
          <div style={{ padding: 32, textAlign: 'center', color: C.textSec }}>
            <p>Your response has been submitted.</p>
            <button type="button" onClick={onBack} style={{ marginTop: 16, fontFamily: 'JetBrains Mono', fontSize: '0.6rem', color: C.textTer }}>
              ← Back to brief
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
